/**
 * PublicRecordDetailPage — single record detail page for a public table.
 * Route: /p/:tableSlug/:recordId
 * No authentication required.
 */

import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicRecordDetail } from '../hooks/usePublicTableView';
import usePublicAction from '../hooks/usePublicAction';
import { applyPageSEO, resolveSeoForRecord } from '../utils/seoHelper';
import { usePublicApp, useCrumbLabel } from '../context/PublicAppContext';
import PublicPageHeader from '../components/public/PublicPageHeader';
import PublicRecordDetail from '../components/public/PublicRecordDetail';
import PublicRecordDetailContainer from '../components/public/PublicRecordDetailContainer';
import PublicCustomLayout from '../components/public/PublicCustomLayout';
import PublicSuccessModal from '../components/public/PublicSuccessModal';
import ScopedCustomStyle from '../components/public/ScopedCustomStyle';
import { buildRelatedScope, failedAliases } from '../utils/relatedScope';
import { startAction } from '../utils/actionRunner';
import PublicLoadingSkeleton from '../components/public/PublicLoadingSkeleton';
import PublicErrorPage from '../components/public/PublicErrorPage';

export default function PublicRecordDetailPage() {
  const { tableSlug, recordId } = useParams();
  const [searchParams] = useSearchParams();
  const previewKey = searchParams.get('pk') || undefined;

  const { data, isLoading, isError, error, refetch } = usePublicRecordDetail(tableSlug, recordId, { previewKey });
  const { setAppBranding } = usePublicApp();

  useEffect(() => {
    if (data?.view_config?.seo) {
      const mapping = data.view_config.field_mapping || {};
      const titleValue = data.record?.[mapping.title_field];
      const branding = data.view_config.branding || {};

      const resolvedSeo = resolveSeoForRecord(data.view_config.seo, data.record);
      const seo = {
        ...resolvedSeo,
        meta_title: titleValue
          ? `${titleValue} | ${branding.page_title || ''}`
          : resolvedSeo.meta_title,
        og_title: titleValue || resolvedSeo.og_title,
      };
      applyPageSEO(seo, data.app_branding);
    }
  }, [data]);

  // Propagate app-level branding to the layout
  useEffect(() => {
    if (data?.app_branding) {
      setAppBranding(data.app_branding);
    }
  }, [data?.app_branding, setAppBranding]);

  // Name the breadcrumb after the record itself, so the trail ends with the
  // record's title rather than its id. Must run before the early returns below.
  useCrumbLabel(data?.record?.[data?.view_config?.field_mapping?.title_field]);

  // The action machinery — session check, sign-in detour and return, duplicate
  // guard, completed set — lives in one hook so the React event detail page
  // runs it too rather than reimplementing it. See hooks/usePublicAction.js.
  const {
    signedIn, actionState, setActionState, completedKeys, submitAction, resumeAfterSignIn,
  } = usePublicAction({
    tableSlug, recordId, actionButtons: data?.view_config?.action_buttons,
  });

  // Finish a submission the visitor started before signing in. In an effect
  // rather than mid-render: a submit is a side effect, and React may render a
  // component more than once before committing it.
  useEffect(() => { resumeAfterSignIn(); }, [resumeAfterSignIn]);

  if (isLoading) return <PublicLoadingSkeleton />;
  if (isError) return <PublicErrorPage error={error} onRetry={refetch} />;

  const { view_config = {}, fields = [], record = {} } = data;
  const { branding = {}, field_mapping = {}, interaction_config = {} } = view_config;
  const detailColumns = Number(interaction_config.detail_columns) || 2;
  const detailLayout = interaction_config.detail_layout === 'container' ? 'container' : 'card';

  // Custom HTML layout. `layout` is null whenever the configured Page Builder
  // page is missing, soft-deleted, a draft, or has no visible HTML section —
  // the server decides that (build_custom_layout_payload), and null means fall
  // through to the built-in renderer below, exactly as
  // PublicViewType.fallback_key specifies. A misconfigured layout degrades; it
  // never blanks the page.
  //
  // `alias` is the table's own name, so the data key is whatever this
  // application calls its table — nothing here is app-specific.
  const customLayout = view_config.view_type === 'custom' ? view_config.layout : null;

  if (customLayout) {
    // The record sits under its table's own name; related blocks sit under the
    // aliases the builder chose. A failed block arrives as an empty list, which
    // the renderer drops — so a flaky related table costs a section, not a page.
    const templateData = { [customLayout.alias]: record, ...buildRelatedScope(data.related) };

    return (
      <div className="container py-4">
        {actionState && !actionState.reveal && (
          <div
            className={`alert ${actionState.status === 'error' ? 'alert-danger' : 'alert-success'}`}
            role="status"
          >
            {actionState.status === 'busy' ? 'Working…' : actionState.message}
          </div>
        )}
        {actionState && actionState.status === 'done' && actionState.reveal && (
        <PublicSuccessModal
          open
          message={actionState?.message}
          // The record's own title, and only when it really is a string. A JSON
          // or array column here would throw "Objects are not valid as a React
          // child" and take the whole page down with it.
          subtitle={typeof record?.[field_mapping?.title_field] === 'string'
            ? record[field_mapping.title_field] : undefined}
          reveal={actionState?.reveal}
          // Only the pass is dismissed, never the 'done' state. Clearing the
          // whole thing restores the pristine Register button, and pressing it
          // again lands on the uniqueness clash that refusal_payload exists to
          // keep visitors out of. Today's inline alert takes over instead.
          onClose={() => setActionState(state => ({ ...state, reveal: null }))}
        />
        )}
        <ScopedCustomStyle css={customLayout.css} scopeId="arcee-custom" />
        <PublicCustomLayout
          html={customLayout.html}
          data={templateData}
          failed={failedAliases(data.related)}
          actionButtons={view_config.action_buttons}
          actionForms={view_config.action_forms}
          placement="detail"
          recordAlias={customLayout.alias}
          formState={actionState}
          completedActions={completedKeys}
          onSubmitForm={submitAction}
          onAction={button => startAction(button, {
            tableSlug, recordId, isAuthenticated: signedIn,
            onSubmit: b => submitAction({ button: b, answers: {}, payload: {} }),
          })}
        />
      </div>
    );
  }

  // The container layout is deliberately roomier than the boxed card at every
  // column count — it has no card chrome to sit inside. Its 1-column variant is
  // the large/showcase treatment, so it gets the widest shell of the three.
  const containerMaxWidth = detailLayout === 'container'
    ? (detailColumns === 3 ? 1320 : detailColumns === 1 ? 1240 : 1080)
    : (detailColumns === 3 ? 1040 : detailColumns === 1 ? 660 : 800);

  return (
    <div className="container py-4" style={{ maxWidth: containerMaxWidth }}>
      {detailLayout === 'container' ? (
        <PublicRecordDetailContainer
          record={record}
          fields={fields}
          mapping={field_mapping}
          branding={branding}
          detailColumns={detailColumns}
        />
      ) : (
        <>
          <PublicPageHeader branding={branding} />
          <PublicRecordDetail
            record={record}
            fields={fields}
            mapping={field_mapping}
            branding={branding}
            tableSlug={tableSlug}
            detailColumns={detailColumns}
          />
        </>
      )}
    </div>
  );
}
